import { Controller } from "@hotwired/stimulus"

/**
 * Keeps a start/end pair the same length apart while the user is editing the
 * start. The common case for events is: staff set a 12:00–13:00 slot once, then
 * move it later — they expect the end to follow, and to still be able to
 * stretch or shorten it afterwards.
 *
 * Works on both field shapes the event forms use:
 *
 *   - `datetime-local` — the one-time occurrence's edit form, where a move can
 *     change the date as well as the clock time.
 *   - `time` — the recurring occurrence's change-time sheet, which only moves
 *     an occurrence within its own day. A pair that reads backwards is one of
 *     the overnight rows this predates (22:00–01:00 is three hours, not minus
 *     twenty-one), so its length is read the same way.
 *
 * An occurrence lives inside one day, so a shift never carries the end past
 * midnight: it stops at 23:59 and the length gives, not the date.
 *
 * The length in force is whatever the two fields currently say, the user's own
 * end edits included. Setting the end to 14:30 and then moving the start is a
 * reschedule of a two-and-a-half-hour occurrence, not a request to shrink it
 * back to the length it was loaded with.
 *
 * An optional `duration` target is written with that length ("2h 30m") on every
 * change, so the pair states what it adds up to instead of leaving it to be
 * worked out from two clock faces.
 *
 * Usage:
 *
 *   <div data-controller="datetime-pair">
 *     <input data-datetime-pair-target="start"
 *            data-action="input->datetime-pair#startChanged">
 *     <input data-datetime-pair-target="end"
 *            data-action="input->datetime-pair#endChanged">
 *     <span data-datetime-pair-target="duration"></span>
 *   </div>
 */
const DAY_MINUTES = 24 * 60

export default class extends Controller<HTMLElement> {
  static targets = ["start", "end", "duration"]
  static values = {
    enabled: { type: Boolean, default: true },
  }

  declare readonly hasStartTarget: boolean
  declare readonly startTarget: HTMLInputElement
  declare readonly hasEndTarget: boolean
  declare readonly endTarget: HTMLInputElement
  declare readonly hasDurationTarget: boolean
  declare readonly durationTarget: HTMLElement
  declare enabledValue: boolean

  // Minutes between start and end, snapshotted on connect and whenever the
  // user last set the end themselves. A start move re-applies it verbatim.
  private durationMinutes: number | null = null
  // Previous value of `start`. An empty one means there is no pair to measure.
  private lastStartValue = ""

  connect() {
    if (!this.enabledValue) return
    this.lastStartValue = this.hasStartTarget ? this.startTarget.value : ""
    this.durationMinutes = this.computeDuration()
    this.renderDuration()
  }

  startChanged() {
    if (!this.enabledValue) return
    if (!this.hasStartTarget || !this.hasEndTarget) return

    const previous = this.lastStartValue
    const next = this.startTarget.value
    this.lastStartValue = next

    if (!next) {
      // Cleared start — leave end alone.
      this.renderDuration()
      return
    }

    if (!this.endTarget.value || !previous) {
      // End is empty, or there was no prior start to measure against. Refresh
      // the duration so the next start edit has something to re-apply.
      this.durationMinutes = this.computeDuration()
      this.renderDuration()
      return
    }

    const start = this.parse(next)
    if (start !== null && this.durationMinutes !== null) {
      this.setEnd(this.format(this.sameDayEnd(start, this.durationMinutes)))
    }
    this.renderDuration()
  }

  endChanged() {
    if (!this.enabledValue) return
    this.durationMinutes = this.computeDuration()
    this.renderDuration()
  }

  private get timeOnly(): boolean {
    return this.hasStartTarget && this.startTarget.type === "time"
  }

  private computeDuration(): number | null {
    if (!this.hasStartTarget || !this.hasEndTarget) return null
    const start = this.parse(this.startTarget.value)
    const end = this.parse(this.endTarget.value)
    if (start === null || end === null) return null

    const span = end - start
    // Clock times carry no date, so a backwards pair is one of the overnight
    // occurrences that predate the same-day rule, not a negative duration.
    return this.timeOnly ? ((span % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES : span
  }

  // What the two fields currently add up to. A pair that reads backwards has no
  // length to state, so the placeholder stands rather than a negative one.
  private renderDuration() {
    if (!this.hasDurationTarget) return

    const minutes = this.computeDuration()
    this.durationTarget.textContent =
      minutes === null || minutes <= 0 ? "—" : formatDuration(minutes)
  }

  // The end of an occurrence starting at `start`, held inside that day. An
  // overnight one edited here is pulled back onto a single day, which is the
  // shape the sheet can express and the server will accept.
  private sameDayEnd(start: number, duration: number): number {
    const dayStart = this.timeOnly ? 0 : Math.floor(start / DAY_MINUTES) * DAY_MINUTES
    return Math.min(start + duration, dayStart + DAY_MINUTES - 1)
  }

  // Minutes, from either field shape. A `datetime-local` value is read as UTC
  // on purpose: these are wall times in the *event's* zone, which the server
  // reads separately, and naive arithmetic keeps a shift off a daylight-saving
  // seam in whatever zone the browser happens to be in.
  private parse(value: string): number | null {
    if (!value) return null

    if (this.timeOnly) {
      const [hours, minutes] = value.split(":").map(Number)
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
      return hours * 60 + minutes
    }

    const parsed = new Date(`${value}Z`)
    return Number.isNaN(parsed.getTime()) ? null : Math.round(parsed.getTime() / 60000)
  }

  private format(minutes: number): string {
    if (this.timeOnly) {
      return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
    }

    const date = new Date(minutes * 60000)
    return (
      `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
      `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
    )
  }

  private setEnd(value: string) {
    if (value === this.endTarget.value) return

    this.endTarget.value = value
    // Notify any sibling controllers listening for input/change so the
    // wizard summary rail picks up the new end time without the user
    // having to focus the field.
    this.endTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.endTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }
}

function pad(n: number): string {
  return String(n).padStart(2, "0")
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours && rest) return `${hours}h ${rest}m`
  return hours ? `${hours}h` : `${rest}m`
}
